import { type StateCreator } from "zustand";

import { dashboardService, type TSectionInitialData } from "@/api/dashboard";
import {
  getSessionExpiredAwareErrorMessage,
  isSessionExpiredHttpError,
  SESSION_EXPIRED_USER_MESSAGE,
} from "@/utils/authUtils";

const getDashboardCacheKey = (sectionId: string, companyId?: string | null) =>
  companyId ? `${companyId}:${sectionId}` : sectionId;

export { getDashboardCacheKey };

export type TDashboardStoreState = {
  dashboardData: Record<string, TSectionInitialData>;
  loadingSections: Set<string>;
  errors: Record<string, string | null>;
};

export type TDashboardStoreActions = {
  fetchSectionDashboard: (
    sectionId: string,
    options?: { force?: boolean; companyId?: string | null }
  ) => Promise<void>;
  getSectionDashboard: (sectionId: string, companyId?: string | null) => TSectionInitialData | null;
  invalidateSectionDashboard: (sectionId: string, companyId?: string | null) => void;
  invalidateAllDashboards: () => void;
};

export type TDashboardStore = TDashboardStoreState & TDashboardStoreActions;

const initialDashboardStoreState: TDashboardStoreState = {
  dashboardData: {},
  loadingSections: new Set(),
  errors: {},
};

export const createDashboardSlice: StateCreator<TDashboardStore> = (set, get) => ({
  ...initialDashboardStoreState,

  fetchSectionDashboard: async (sectionId: string, options = {}) => {
    const { force = false, companyId } = options;
    const cacheKey = getDashboardCacheKey(sectionId, companyId);
    const state = get();

    // Ensure loadingSections is always a Set (in case it was deserialized incorrectly)
    const loadingSections = state.loadingSections instanceof Set ? state.loadingSections : new Set<string>();

    // Prevent concurrent fetches for the same section/company
    if (loadingSections.has(cacheKey)) {
      return;
    }

    // Skip if dashboard data exists and not forcing refresh (works like profile fetch)
    if (!force && state.dashboardData[cacheKey]) {
      return;
    }

    // Mark section as loading
    set({
      loadingSections: new Set([...loadingSections, cacheKey]),
      errors: { ...state.errors, [cacheKey]: null },
    });

    try {
      const { data, error } = await dashboardService.getSectionInitialData(sectionId, companyId);

      if (error) {
        if (isSessionExpiredHttpError(error)) {
          set((prev) => {
            const prevLoadingSections =
              prev.loadingSections instanceof Set ? prev.loadingSections : new Set<string>();
            return {
              loadingSections: new Set([...prevLoadingSections].filter((id) => id !== cacheKey)),
              errors: { ...prev.errors, [cacheKey]: SESSION_EXPIRED_USER_MESSAGE },
            };
          });
          return;
        }

        const errorMessage = await getSessionExpiredAwareErrorMessage(error, "Failed to load dashboard data");
        set((prev) => {
          const prevLoadingSections = prev.loadingSections instanceof Set ? prev.loadingSections : new Set<string>();
          return {
            loadingSections: new Set([...prevLoadingSections].filter((id) => id !== cacheKey)),
            errors: { ...prev.errors, [cacheKey]: errorMessage },
          };
        });
        return;
      }

      if (data?.data) {
        set((prev) => {
          const prevLoadingSections = prev.loadingSections instanceof Set ? prev.loadingSections : new Set<string>();
          const newDashboardData = { ...prev.dashboardData };
          newDashboardData[cacheKey] = data.data;
          return {
            dashboardData: newDashboardData,
            loadingSections: new Set([...prevLoadingSections].filter((id) => id !== cacheKey)),
            errors: { ...prev.errors, [cacheKey]: null },
          };
        });
      }
    } catch (err) {
      if (isSessionExpiredHttpError(err)) {
        set((prev) => {
          const prevLoadingSections = prev.loadingSections instanceof Set ? prev.loadingSections : new Set<string>();
          return {
            loadingSections: new Set([...prevLoadingSections].filter((id) => id !== cacheKey)),
            errors: { ...prev.errors, [cacheKey]: SESSION_EXPIRED_USER_MESSAGE },
          };
        });
        return;
      }

      const errorMessage = err instanceof Error ? err.message : "Failed to load dashboard data";
      set((prev) => {
        const prevLoadingSections = prev.loadingSections instanceof Set ? prev.loadingSections : new Set<string>();
        return {
          loadingSections: new Set([...prevLoadingSections].filter((id) => id !== cacheKey)),
          errors: { ...prev.errors, [cacheKey]: errorMessage },
        };
      });
    }
  },

  getSectionDashboard: (sectionId: string, companyId?: string | null) => {
    return get().dashboardData[getDashboardCacheKey(sectionId, companyId)] ?? null;
  },

  invalidateSectionDashboard: (sectionId: string, companyId?: string | null) => {
    const cacheKey = getDashboardCacheKey(sectionId, companyId);
    set((prev) => {
      const newDashboardData = { ...prev.dashboardData };
      delete newDashboardData[cacheKey];
      const newErrors = { ...prev.errors };
      delete newErrors[cacheKey];
      return {
        dashboardData: newDashboardData,
        errors: newErrors,
      };
    });
  },

  invalidateAllDashboards: () => {
    set({
      dashboardData: {},
      errors: {},
      loadingSections: new Set(),
    });
  },
});
